import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Container, Table, Form, InputGroup, Button, Row, Col, Pagination, Badge, Modal, Spinner, Alert } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import apiClient from '../../../../api/apiClient';
import { showToast } from '../../../../utils/toast';

const DistributorSupports = () => {
  const [supportsList, setSupportsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedSupport, setSelectedSupport] = useState(null);
  
  const [formData, setFormData] = useState({
    mobile: '',
    password: '',
    customer_id: '',
    name: '',
    email: '',
    postal_code: '',
    status: 'active'
  });
  
  const [formErrors, setFormErrors] = useState({});

  // Fetch Support users from API
  const fetchSupportUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        role: 'support',
        page: currentPage,
        page_size: rowsPerPage
      };
      
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;
      
      const response = await apiClient.get('/api/distributor/users/', { params });
      
      if (response.data) {
        setSupportsList(response.data.results || []);
        setTotalCount(response.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching Support users:', error);
      showToast.error('Failed to fetch Support users');
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, searchQuery, statusFilter]);

  // Debounced effect
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSupportUsers();
    }, searchQuery ? 300 : 0);

    return () => clearTimeout(timer);
  }, [fetchSupportUsers]);

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };
  
  const handleFilterChange = (filterType, value) => {
    if (filterType === 'status') setStatusFilter(value);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };


  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(supportsList);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Support');
    XLSX.writeFile(workbook, `support_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleAdd = () => {
    setSelectedSupport(null);
    setModalMode('add');
    setFormData({
      mobile: '',
      password: '',
      customer_id: '',
      name: '',
      email: '',
      postal_code: '',
      status: 'active'
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (support) => {
    setSelectedSupport(support);
    setModalMode('edit');
    setFormData({
      mobile: support.mobile,
      password: '', // Don't populate password for edit
      customer_id: support.customer_id || '',
      name: support.name,
      email: support.email || '',
      postal_code: '',
      status: support.status
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (support) => {
    if (window.confirm(`Are you sure you want to deactivate ${support.name}?`)) {
      try {
        await apiClient.delete(`/api/distributor/users/${support.id}/`);
        showToast.success(`${support.name} deactivated successfully`);
        fetchSupportUsers();
      } catch (error) {
        console.error('Error deactivating Support:', error);
        showToast.error('Failed to deactivate Support');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormErrors({});
    
    try {
      const payload = {
        ...formData,
        role: 'support'
      };
      
      // Remove empty password for edit mode
      if (modalMode === 'edit' && !payload.password) {
        delete payload.password;
      }
      
      let response;
      if (modalMode === 'add') {
        response = await apiClient.post('/api/distributor/users/', payload);
        showToast.success('Support created successfully');
      } else {
        response = await apiClient.put(`/api/distributor/users/${selectedSupport.id}/`, payload);
        showToast.success('Support updated successfully');
      }
      
      setShowModal(false);
      fetchSupportUsers();
    } catch (error) {
      console.error('Error saving Support:', error);
      
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        showToast.error(error.response.data.message);
      } else {
        showToast.error(`Failed to ${modalMode === 'add' ? 'create' : 'update'} Support`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getStatusBadge = (status) => {
    return status === 'active' ? 
      <Badge bg="success">Active</Badge> : 
      <Badge bg="danger">Inactive</Badge>;
  };

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-headset me-2"></i>
          Support Staff
        </h5>
        <div className="d-flex gap-2">
          <Button variant="success" onClick={exportToExcel}>
            <i className="fas fa-file-excel me-2"></i>
            Export Excel
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            <i className="fas fa-plus me-2"></i>
            Add Support
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Row className="mb-4">
        <Col md={3}>
          <InputGroup>
            <InputGroup.Text>
              <i className="fas fa-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search Support..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Form.Select>
        </Col>
        <Col md={7}>
          {/* Space for future filters */}
        </Col>
      </Row>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover className="modern-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>Customer ID</th>
                <th>Name</th>
                <th>Contact</th>
                <th>Location</th>
                <th>Status</th>
                <th>Last Login</th>
              </tr>
            </thead>
            <tbody>
              {supportsList.map((support) => (
                <tr key={support.id}>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(support)}
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(support)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </div>
                  </td>
                  <td><strong>{support.customer_id}</strong></td>
                  <td>
                    <div>
                      <div>{support.name}</div>
                      <small className="text-muted">{support.email}</small>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>{support.mobile}</div>
                      <small className="text-muted">ID: {support.id}</small>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>{support.city}, {support.district}</div>
                      <small className="text-muted">{support.region}</small>
                    </div>
                  </td>
                  <td>{getStatusBadge(support.status)}</td>
                  <td>
                    <small className="text-muted">
                      {support.last_login_display || 'Never'}
                    </small>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {supportsList.length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted">No Support users found</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <Form.Select
            value={rowsPerPage}
            onChange={(e) => {
              const newRowsPerPage = Number(e.target.value);
              setRowsPerPage(newRowsPerPage);
              setCurrentPage(1);
            }}
            style={{ width: 'auto' }}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </Form.Select>
        </div>
        <div>
          Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} entries
        </div>
        <Pagination>
          <Pagination.First 
            onClick={() => setCurrentPage(1)} 
            disabled={currentPage === 1} 
          />
          <Pagination.Prev 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
            disabled={currentPage === 1} 
          />
          {[...Array(Math.min(5, totalPages))].map((_, index) => {
            const pageNum = Math.max(1, Math.min(currentPage - 2 + index, totalPages - 4 + index));
            return (
              <Pagination.Item
                key={pageNum}
                active={pageNum === currentPage}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </Pagination.Item>
            );
          })}
          <Pagination.Next 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
            disabled={currentPage === totalPages} 
          />
          <Pagination.Last 
            onClick={() => setCurrentPage(totalPages)} 
            disabled={currentPage === totalPages} 
          />
        </Pagination>
      </div>

      {/* Support Form Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'add' ? 'Add New' : 'Edit'} Support Staff</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {Object.keys(formErrors).length > 0 && (
              <Alert variant="danger">
                <ul className="mb-0">
                  {Object.entries(formErrors).map(([field, errors]) => (
                    <li key={field}>{field}: {Array.isArray(errors) ? errors.join(', ') : errors}</li>
                  ))}
                </ul>
              </Alert>
            )}
            
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Mobile Number *</Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => handleInputChange('mobile', e.target.value)}
                    placeholder="9876543210"
                    maxLength={10}
                    disabled={modalMode === 'edit'}
                    isInvalid={formErrors.mobile}
                    required
                  />
                </Form.Group>
              </Col>
              
              {modalMode === 'add' && (
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Password *</Form.Label>
                    <Form.Control
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Enter password"
                      isInvalid={formErrors.password}
                      required
                    />
                  </Form.Group>
                </Col>
              )}
              
              {modalMode === 'edit' && (
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>New Password (optional)</Form.Label>
                    <Form.Control
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Enter new password to change"
                    />
                  </Form.Group>
                </Col>
              )}
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Customer ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.customer_id}
                    onChange={(e) => handleInputChange('customer_id', e.target.value)}
                    placeholder="SUP001"
                    isInvalid={formErrors.customer_id}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Full Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter full name"
                    isInvalid={formErrors.name}
                    required
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                    isInvalid={formErrors.email}
                  />
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => handleInputChange('postal_code', e.target.value)}
                    placeholder="110001"
                    maxLength={6}
                  />
                </Form.Group>
              </Col>
              
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {modalMode === 'add' ? 'Creating...' : 'Updating...'}
                </>
              ) : (
                <>
                  {modalMode === 'add' ? 'Create' : 'Update'} Support
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

    </Container>
  );
};

export default DistributorSupports;